import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { speichereInvestition } from 'modules/investitionen/features/investitionenSlice';
import { formatKurs } from 'core/utils/format';

const inputKlasse =
  'w-full rounded-lg border border-line bg-paper px-3 py-2 text-ink focus:border-brand-600 focus:outline-none';

/** Ersetzt eine Position (per id) im Array oder haengt sie an. */
const mitPosition = (positionen, pos) => {
  const idx = positionen.findIndex((p) => p.id === pos.id);
  if (idx < 0) return [...positionen, pos];
  const kopie = [...positionen];
  kopie[idx] = pos;
  return kopie;
};

/**
 * Modaler Editor fuer eine einzelne Position eines Bereichs. Der aktuelle Wert
 * kann manuell gepflegt oder per Verknuepfung mit einer Aktien-Depot-Position
 * (ISIN) automatisch aus dem Depot gezogen werden.
 */
export default function PositionForm({ bereich, position, depotWerte, onClose }) {
  const dispatch = useDispatch();
  const [bezeichnung, setBezeichnung] = useState(position.bezeichnung || '');
  const [investiert, setInvestiert] = useState(position.investiert ?? '');
  const [aktuellerWert, setAktuellerWert] = useState(position.aktuellerWert ?? '');
  const [isin, setIsin] = useState(position.isin || '');
  const [datum, setDatum] = useState(position.datum || '');
  const [notiz, setNotiz] = useState(position.notiz || '');
  const [fehler, setFehler] = useState(null);
  const [laeuft, setLaeuft] = useState(false);

  const depotOptionen = Object.entries(depotWerte || {});
  const verknuepft = isin ? depotWerte?.[isin] : null;

  const waehleIsin = (wert) => {
    setIsin(wert);
    if (wert && depotWerte[wert] && !bezeichnung.trim()) setBezeichnung(depotWerte[wert].aktienName);
  };

  const speichern = async (e, positionen) => {
    e?.preventDefault?.();
    setFehler(null);
    setLaeuft(true);
    try {
      await dispatch(speichereInvestition({ ...bereich, positionen })).unwrap();
      onClose();
    } catch (ex) {
      setFehler('Speichern fehlgeschlagen. Bitte erneut versuchen.');
      setLaeuft(false);
    }
  };

  const absenden = (e) => {
    if (!bezeichnung.trim()) {
      e.preventDefault();
      setFehler('Bitte eine Bezeichnung angeben.');
      return;
    }
    const neu = {
      id: position.id,
      bezeichnung: bezeichnung.trim(),
      investiert: investiert === '' ? null : Number(investiert),
      aktuellerWert: verknuepft ? null : aktuellerWert === '' ? null : Number(aktuellerWert),
      isin: isin || null,
      datum: datum || null,
      notiz: notiz.trim(),
    };
    speichern(e, mitPosition(bereich.positionen, neu));
  };

  const entfernen = (e) => {
    if (!window.confirm('Diese Position löschen?')) return;
    speichern(e, bereich.positionen.filter((p) => p.id !== position.id));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-paper p-6 shadow-xl">
        <h2 className="mb-4 font-serif text-xl font-semibold text-ink">
          {position.bezeichnung ? 'Position bearbeiten' : 'Position hinzufügen'}
        </h2>

        <form onSubmit={absenden} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Bezeichnung</label>
            <input
              value={bezeichnung}
              onChange={(e) => setBezeichnung(e.target.value)}
              placeholder="z. B. MSCI World ETF, Bitcoin, Sparbrief 2025"
              className={inputKlasse}
              autoFocus
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Verknüpfung mit Aktien-Depot (optional)</label>
            <select value={isin} onChange={(e) => waehleIsin(e.target.value)} className={inputKlasse}>
              <option value="">Keine – aktueller Wert manuell</option>
              {depotOptionen.map(([code, d]) => (
                <option key={code} value={code}>
                  {d.aktienName} ({code})
                </option>
              ))}
            </select>
            {verknuepft && (
              <p className="mt-1 text-xs text-ink-muted">
                Aktueller Wert wird live aus dem Depot gezogen: {formatKurs(verknuepft.aktuellerWert, verknuepft.waehrung)}
              </p>
            )}
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-sm font-medium text-ink">Investiert</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={investiert}
                onChange={(e) => setInvestiert(e.target.value)}
                placeholder="0,00"
                className={inputKlasse}
              />
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-sm font-medium text-ink">Aktueller Wert</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={verknuepft ? '' : aktuellerWert}
                onChange={(e) => setAktuellerWert(e.target.value)}
                placeholder={verknuepft ? 'aus Depot' : '0,00'}
                disabled={!!verknuepft}
                className={`${inputKlasse} disabled:bg-paper-sunken disabled:text-ink-muted`}
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Datum (Kauf/Anlage)</label>
            <input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} className={inputKlasse} />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Notiz</label>
            <textarea value={notiz} onChange={(e) => setNotiz(e.target.value)} rows={2} className={`${inputKlasse} text-sm`} />
          </div>

          {fehler && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{fehler}</p>}

          <div className="flex items-center justify-between gap-3 pt-1">
            {position.bezeichnung ? (
              <button
                type="button"
                onClick={entfernen}
                disabled={laeuft}
                className="rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50 disabled:opacity-50"
              >
                Löschen
              </button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-line px-3 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-ink/5"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={laeuft}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500 disabled:opacity-50"
              >
                {laeuft ? 'Speichere…' : 'Speichern'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
